import Icon from '@/common/components/Icon';
import { Colors } from '@/common/colors/colors';
import { formatDate } from '@/common/functions/dateFormater';
import { BOOLEAN_LABEL } from '../../../constants/booleanLabels';
import {
	BAD_REVIEWS_CATEGORY,
	SERP_RESULT_CATEGORIES,
} from '../../../constants/categories';
import {
	GroupRow,
	ResultRow,
	SummaryRow,
} from '../../../SerpMonitoringReport.styles';
import LazySelectInput from '../components/lazySelectInput/LazySelectInput';

const CATEGORY_OPTIONS = SERP_RESULT_CATEGORIES.map((category) => ({
	label: category,
	value: category,
}));

const groupResultsByDate = (mainResults) =>
	mainResults.reduce((groups, result) => {
		const date = formatDate(result.date, 'YYYY-MM-DD');
		const dateGroup = groups.find((group) => group.date === date);

		if (dateGroup) {
			dateGroup.results.push(result);
			return groups;
		}

		groups.push({ date, results: [result] });
		return groups;
	}, []);

const buildResultRows = ({
	result,
	expandedIds,
	onToggleResult,
	onCategoryChange,
}) => {
	const isExpanded = !Boolean(expandedIds[result._id]);
	const groupRow = (
		<GroupRow
			key={`${result._id}-group`}
			onClick={() => onToggleResult(result._id)}
		>
			<td>
				<Icon name={isExpanded ? 'chevron-down' : 'chevron-up'} />
			</td>
			<td>{formatDate(result.date, 'YYYY-MM-DD')}</td>
			<td>{result.key_word}</td>
			<td />
			<td />
			<td />
			<td />
			<td />
			<td />
			<td />
			<td>{result.score}</td>
			<td />
		</GroupRow>
	);

	if (!isExpanded) {
		return [groupRow];
	}

	const resultRows = result.search_results.map((searchResult) => {
		const isBadReview = searchResult.category === BAD_REVIEWS_CATEGORY;
		const rowColor = isBadReview ? Colors.red : Colors.white;

		return (
			<ResultRow
				key={`${result._id}-${searchResult.index}`}
				$isBadReview={isBadReview}
				onClick={(event) => event.stopPropagation()}
				style={{ backgroundColor: rowColor }}
			>
				<td />
				<td>{formatDate(result.date, 'YYYY-MM-DD')}</td>
				<td>{result.key_word}</td>
				<td>{searchResult.index || '-'}</td>
				<td>{searchResult.type}</td>
				<td>{searchResult.title}</td>
				<td>
					<LazySelectInput
						ariaLabel='Category'
						options={CATEGORY_OPTIONS}
						selected={CATEGORY_OPTIONS.find(
							(option) => option.value === searchResult.category,
						)}
						setSelected={(option) =>
							onCategoryChange({
								resultId: result._id,
								index: searchResult.index,
								category: option.value,
							})
						}
					/>
				</td>
				<td>{searchResult.points}</td>
				<td>
					<a href={searchResult.link} target='_blank' rel='noreferrer'>
						{searchResult.link.length > 50 ? `${searchResult.link.slice(0, 50)}...` : searchResult.link}
					</a>
				</td>
				<td>
					{searchResult.blacklist_word ? BOOLEAN_LABEL.YES : BOOLEAN_LABEL.NO}
				</td>
				<td />
				<td />
			</ResultRow>
		);
	});

	return [groupRow, ...resultRows];
};

export const getRows = ({
	mainResults,
	dayResults,
	expandedIds,
	onToggleResult,
	onCategoryChange,
}) =>
	groupResultsByDate(mainResults).flatMap(({ date, results }) => {
		const rows = results.flatMap((result) =>
			buildResultRows({
				result,
				expandedIds,
				onToggleResult,
				onCategoryChange,
			}),
		);

		const dayResult = dayResults.find(
			(result) => formatDate(result.date, 'YYYY-MM-DD') === date,
		);

		const summaryRow = (
			<SummaryRow key={`${date}-summary`}>
				<td />
				<td>{date}</td>
				<td />
				<td />
				<td />
				<td />
				<td />
				<td />
				<td />
				<td />
				<td>{dayResult?.score}</td>
				<td>{dayResult?.grade}</td>
			</SummaryRow>
		);

		return [...rows, summaryRow];
	});
